import React, { Fragment, useEffect, useState } from 'react';

import { makeClassNames } from '../../../utils/styles';
import { getCurrentPageNumber as getCurrentPageNumberFromUrl } from '../../../utils/urls-utils';
import ArrowLink from '../../atoms/arrow-link/arrow-link';
import PagerLink from '../../atoms/pager-link/pager-link';

import './pager.scss';

export interface IPager {
  links: string[];
  pageNumber?: number;
  onPageNumberChanged?: (pageNumber: number) => any;
}

const classNames = () =>
  makeClassNames({
    pager: 'pager',
    pagerDots: 'pager__dots',
    pagerArrowWrapper: 'pager__arrow-wrapper',
  });

const cn = classNames();

const PagerLinks = ({
  onClickCallback,
  links,
  currentIndex,
  startIndex = 1,
}: {
  links: string[];
  currentIndex: number;
  startIndex?: number;
  onClickCallback?: (index: number | string) => any;
  useUrl?: boolean;
}) => (
  <Fragment>
    {links.map((link, i) => {
      /* FIXME: key=index */
      const isCurrentPage = i + startIndex === currentIndex + 1;
      return (
        <PagerLink
          key={i}
          id={i + startIndex}
          onClickCallback={onClickCallback}
          label={i + startIndex}
          isCurrentPage={isCurrentPage}
          link={link}
        />
      );
    })}
  </Fragment>
);

const PagerDots = () => <span className={cn.pagerDots}>...</span>;

const getPageNumber = (callback: Function | undefined, pageNumber: number | undefined): number => {
  if (callback && pageNumber) {
    return pageNumber;
  } else if (callback) {
    return 1;
  } else {
    return -1;
  }
};

const Pager = ({ onPageNumberChanged, links, pageNumber }: IPager) => {
  const [selectedPageNumber, setSelectedPageNumber] = useState(getPageNumber(onPageNumberChanged, pageNumber));
  const VISIBLE_NUMBERS = 3;
  const linksLength = links.length;

  const currentIndex = selectedPageNumber - 1;
  const firstPageLink = links[0];
  const lastPageLink = links[linksLength - 1];
  const prevPageLink = currentIndex === 0 ? null : links[currentIndex - 1];
  const nextPageLink = currentIndex === linksLength - 1 ? null : links[currentIndex + 1];
  const currentPageLink = links[currentIndex];

  const isFirstThree = currentIndex < VISIBLE_NUMBERS;
  const isLastThree = currentIndex >= linksLength - VISIBLE_NUMBERS;
  let pager = null;
  const useCallback = Boolean(onPageNumberChanged);

  const onItemClickedCallback = useCallback
    ? (index: number) => {
        if (onPageNumberChanged && index !== selectedPageNumber) {
          setSelectedPageNumber(index);
          onPageNumberChanged(index);
        }
      }
    : undefined;

  /** Use side effect to determinate page number in URL */
  useEffect(() => {
    if (links.length && !useCallback) {
      const computedPageNumber = getCurrentPageNumberFromUrl();
      if (computedPageNumber > links.length) {
        setSelectedPageNumber(links.length);
      } else {
        setSelectedPageNumber(computedPageNumber);
      }
    } else {
      setSelectedPageNumber(selectedPageNumber || -1);
    }
  }, [links, useCallback, selectedPageNumber]);

  /** Define the pager */
  if (linksLength <= 5) {
    pager = <PagerLinks links={links} currentIndex={currentIndex} onClickCallback={onItemClickedCallback} />;
  } else if (isFirstThree) {
    pager = (
      <Fragment>
        <PagerLinks
          links={[...links].slice(0, VISIBLE_NUMBERS)}
          currentIndex={currentIndex}
          onClickCallback={onItemClickedCallback}
        />
        <PagerDots />
        <PagerLinks
          links={[lastPageLink]}
          currentIndex={currentIndex}
          startIndex={linksLength}
          onClickCallback={onItemClickedCallback}
        />
      </Fragment>
    );
  } else if (isLastThree) {
    pager = (
      <Fragment>
        <PagerLinks links={[firstPageLink]} currentIndex={currentIndex} onClickCallback={onItemClickedCallback} />
        <PagerDots />
        <PagerLinks
          links={[...links].slice(linksLength - VISIBLE_NUMBERS)}
          currentIndex={currentIndex}
          startIndex={linksLength - VISIBLE_NUMBERS + 1}
          onClickCallback={onItemClickedCallback}
        />
      </Fragment>
    );
  } else {
    pager = (
      <Fragment>
        <PagerLinks links={[firstPageLink]} currentIndex={currentIndex} onClickCallback={onItemClickedCallback} />
        <PagerDots />
        <PagerLinks
          links={[currentPageLink]}
          currentIndex={currentIndex}
          startIndex={currentIndex + 1}
          onClickCallback={onItemClickedCallback}
        />
        <PagerDots />
        <PagerLinks
          links={[lastPageLink]}
          currentIndex={currentIndex}
          startIndex={linksLength}
          onClickCallback={onItemClickedCallback}
        />
      </Fragment>
    );
  }

  const onArrowClickedPrev = () => {
    if (onPageNumberChanged) {
      setSelectedPageNumber(selectedPageNumber - 1);
      onPageNumberChanged(selectedPageNumber - 1);
    }
  };

  const onArrowClickedNext = () => {
    if (onPageNumberChanged) {
      setSelectedPageNumber(selectedPageNumber + 1);
      onPageNumberChanged(selectedPageNumber + 1);
    }
  };

  return links.length && selectedPageNumber !== -1 ? (
    <div className={cn.pager}>
      <span className={cn.pagerArrowWrapper}>
        {prevPageLink && <ArrowLink reverse link={!useCallback ? prevPageLink : null} onClick={onArrowClickedPrev} />}
      </span>
      {pager}
      <span className={cn.pagerArrowWrapper}>
        {nextPageLink && <ArrowLink link={!useCallback ? nextPageLink : null} onClick={onArrowClickedNext} />}
      </span>
    </div>
  ) : null;
};

export default Pager;
